import { SCROLL_AXES, type ScrollAxis } from "./scroll-box-axes"
import { contentSpans, isContentLeaf, isShownToUsers } from "./scroll-box-content-leaves"
import type { CutPopupFinding } from "./scroll-box-findings"
import { identifyScrollBox } from "./scroll-box-identity"
import { descendantsNearView } from "./scroll-box-near-view"
import { overhangPx, rectSpan, scrollportSpan, type PixelSpan } from "./scroll-box-spans"
import {
  OVERFLOW_THRESHOLD_PX,
  clipsOrScrollsAlong,
  escapesClipOf,
  isAbsolutelyPositioned,
  isPageRoot,
} from "./scroll-box-styles"

interface CutPopup {
  popup: Element
  cutOffPx: number
}

function isLayeredPopup(element: Element): boolean {
  return isAbsolutelyPositioned(element) && getComputedStyle(element).zIndex !== "auto" && isShownToUsers(element)
}

function hangsOutOfItsAnchor(popup: HTMLElement, axis: ScrollAxis): boolean {
  const anchor = popup.offsetParent
  if (anchor === null) return false
  return overhangPx(rectSpan(popup.getBoundingClientRect(), axis), scrollportSpan(anchor, axis)) > OVERFLOW_THRESHOLD_PX
}

function hasNoCloserCutter(popup: Element, container: Element, axis: ScrollAxis): boolean {
  for (let ancestor = popup.parentElement; ancestor && ancestor !== container; ancestor = ancestor.parentElement) {
    if (clipsOrScrollsAlong(ancestor, axis)) return false
  }
  return true
}

function popupContentSpans(popup: Element, axis: ScrollAxis): PixelSpan[] {
  return [popup, ...Array.from(popup.querySelectorAll("*"))].filter(isContentLeaf).flatMap((leaf) => contentSpans(leaf, axis))
}

function popupsCutBy(container: Element, axis: ScrollAxis): CutPopup[] {
  const visible = scrollportSpan(container, axis)
  return descendantsNearView(container).flatMap((candidate) => {
    if (!(candidate instanceof HTMLElement) || !isLayeredPopup(candidate)) return []
    if (escapesClipOf(candidate, container) || !hangsOutOfItsAnchor(candidate, axis)) return []
    if (!hasNoCloserCutter(candidate, container, axis)) return []
    const cutOffPx = Math.max(0, ...popupContentSpans(candidate, axis).map((span) => overhangPx(span, visible)))
    return cutOffPx > OVERFLOW_THRESHOLD_PX ? [{ popup: candidate, cutOffPx }] : []
  })
}

export function detectCutPopups(container: Element): CutPopupFinding[] {
  if (isPageRoot(container) || !isShownToUsers(container)) return []
  return SCROLL_AXES.filter((axis) => clipsOrScrollsAlong(container, axis)).flatMap((axis) => {
    const cutPopups = popupsCutBy(container, axis)
    if (cutPopups.length === 0) return []
    const mostCut = cutPopups.reduce((largest, cutPopup) => (cutPopup.cutOffPx > largest.cutOffPx ? cutPopup : largest))
    const finding: CutPopupFinding = {
      kind: "cut-popup",
      identity: identifyScrollBox(container),
      boxWidthPx: container.clientWidth,
      boxHeightPx: container.clientHeight,
      axis: axis.name,
      popupKey: identifyScrollBox(mostCut.popup).key,
      cutOffPx: mostCut.cutOffPx,
    }
    return [finding]
  })
}
